import { useCallback, useState } from 'react'

import { externalAuthApi } from '../api'
import type { AuthResponseData } from '../api/types'
import { BASE_PATH_B2B, DEFAULT_ERROR_MESSAGE, ROUTES } from '../constants'
import { type UseAuthBaseOptions, useAuthBase } from './useAuthBase'

export type UseSignUpOrSignInOptions = {
  basePath?: string
  allowsOtp?: boolean
  onAccountExists?: (email: string) => void
  onGoogleLoginRequired?: (email: string) => void
  onSignupEmailSent?: (email: string) => void
  onOtpRequired?: (email: string) => void
  onRateLimited?: () => void
  onCtaResolved?: (ctaType: 'signup' | 'login') => void
} & UseAuthBaseOptions

interface UseSignUpOrSignInReturn {
  email: string
  isLoading: boolean
  error: string | undefined
  handleEmailSubmit: (email: string) => Promise<void>
}

export function useSignUpOrSignIn(options: UseSignUpOrSignInOptions = {}): UseSignUpOrSignInReturn {
  const {
    nextUrl,
    scope,
    initialEmail,
    allowsOtp,
    onAccountExists,
    onGoogleLoginRequired,
    onSignupEmailSent,
    onOtpRequired,
    onRateLimited,
    onCtaResolved,
  } = options
  const [email, setEmail] = useState<string>(initialEmail || '')
  const {
    isLoading,
    error,
    setError,
    buildEventData,
    navigateToCallback,
    redirectToCallback,
    handleAsyncOperation,
  } = useAuthBase(options)

  const verifyEmailSentPath =
    options.basePath === BASE_PATH_B2B
      ? ROUTES.B2B.SIGN_UP.VERIFY_EMAIL_REQUEST_SENT
      : ROUTES.SIGN_UP.VERIFY_EMAIL_REQUEST_SENT

  const handleRedirectOrEmailValidation = useCallback(
    (data: AuthResponseData | undefined, emailValue: string) => {
      if (data?.mustRedirectTo) {
        redirectToCallback(data.mustRedirectTo)
        return true
      }
      setEmail(emailValue)
      onCtaResolved?.('signup')
      if (data?.confirmationMethod === 'otp') {
        // Step-based callers (the iframe) supply onOtpRequired to render the OTP
        // form in place; the standalone page falls back to a URL navigation.
        if (onOtpRequired) {
          onOtpRequired(emailValue)
        } else {
          navigateToCallback(ROUTES.SIGN_UP.VERIFY_EMAIL_OTP, { email: emailValue })
        }
      } else if (onSignupEmailSent) {
        onSignupEmailSent(emailValue)
      } else {
        navigateToCallback(verifyEmailSentPath, { email: emailValue })
      }
      return false
    },
    [
      redirectToCallback,
      navigateToCallback,
      onSignupEmailSent,
      onOtpRequired,
      onCtaResolved,
      verifyEmailSentPath,
    ]
  )

  const handleEmailSubmit = useCallback(
    async (emailValue: string) => {
      const result = await handleAsyncOperation(async () => {
        return await externalAuthApi.signUpOrSignInByEmail({
          email: emailValue,
          nextUrl,
          scope,
          event_data: buildEventData('SignUpModal'),
          allowsOtp,
        })
      })

      if (!result) {
        return
      }

      const { response, data } = result

      if (response.status === 429 && data?.redirectToErrorPage) {
        if (onRateLimited) {
          onRateLimited()
        } else {
          navigateToCallback(ROUTES.SIGN_UP.SIGNUP_ATTEMPTS_ERROR)
        }
        return
      }

      if (data?.accountAlreadyExists === true) {
        setEmail(emailValue)
        onCtaResolved?.('login')
        if (data.loginMethod === 'GOOGLE_ONLY') {
          // Step-based callers (the iframe) supply onGoogleLoginRequired to render the
          // forced Google screen in place; the standalone page falls back to a URL navigation.
          if (onGoogleLoginRequired) {
            onGoogleLoginRequired(emailValue)
          } else {
            navigateToCallback(ROUTES.SIGN_IN.GOOGLE_SIGN_IN, { email: emailValue })
          }
        } else if (onAccountExists) {
          onAccountExists(emailValue)
        } else {
          navigateToCallback(ROUTES.SIGN_IN.PASSWORD_SIGN_IN, { email: emailValue })
        }
        return
      }

      if (
        response.ok &&
        (data?.accountAlreadyExists === false || data?.accountAlreadyExists === undefined)
      ) {
        handleRedirectOrEmailValidation(data, emailValue)
        return
      }

      setError(data?.message || DEFAULT_ERROR_MESSAGE)
    },
    [
      nextUrl,
      scope,
      allowsOtp,
      buildEventData,
      handleAsyncOperation,
      navigateToCallback,
      onAccountExists,
      onGoogleLoginRequired,
      onRateLimited,
      onCtaResolved,
      handleRedirectOrEmailValidation,
      setError,
    ]
  )

  return {
    email,
    isLoading,
    error,
    handleEmailSubmit,
  }
}
